import { useEffect, useId, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { plainRate, useQuoteLookup, type FxQuote } from "@/api/fx";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { formatDate, formatInr, formatMoney } from "@/lib/format";

export type RupeeRates = { fx_rate?: string; received_rate?: string };

/** How the rupee rate is worked out: through another currency (SAR rate × rupees per SAR), or
 *  straight from the invoice currency to rupees. Either way only `fx_rate` is saved. */
export type RupeePath = "via" | "direct";

/** What "via" goes through when the money came in the invoice currency itself. */
export const VIA_CURRENCY = "SAR";

/** The directors' practice today: money handed over in riyals is valued through the riyal.
 *  When they convert dollars straight to rupees instead, return "direct" here. */
export function defaultPath(receivedIn: string): RupeePath {
  return receivedIn ? "via" : "direct";
}

/** Which figure the rupee rate follows: typed (or fetched) itself, the SAR rate × rupees per
 *  SAR, or the rupees received ÷ the net amount. */
type Driver = "rate" | "chain" | "rupees";
type Fetched = { perUnit?: string; perOther?: string; fx?: string };

type RupeeValueProps = {
  id: string;
  /** The invoice currency; `fx_rate` is rupees per one of it. */
  currency: string;
  /** What reached the account, in the invoice currency (amount less bank charges). */
  net: string | null;
  receivedOn: string;
  /** What was handed over when it is not the invoice currency, e.g. SAR, at `receivedRate`
   *  units per one of the invoice currency. */
  receivedCurrency: string;
  receivedRate: string;
  /** A recorded payment's riyal rate cannot change; only the rupee rate is filled then. */
  receivedRateFixed?: boolean;
  fxRate: string;
  /** The bank's own rupee figure (eFIRA), which counts instead of the rate when given. */
  inrCredited: string;
  error?: string;
  onChange: (rates: RupeeRates) => void;
};

/** Six decimals at most, as the API stores a rate, written as a person would. */
function rateText(value: number): string {
  return plainRate(value.toFixed(6));
}

function positive(text: string | null): number | null {
  const value = Number(text);
  return text && text.trim() !== "" && Number.isFinite(value) && value > 0 ? value : null;
}

function times(a: string, b: string): string | null {
  const [x, y] = [positive(a), positive(b)];
  return x && y ? rateText(x * y) : null;
}

function per(a: string, b: string | null): string | null {
  const [x, y] = [positive(a), positive(b)];
  return x && y ? rateText(x / y) : null;
}

/** A fetch fills a figure only while it is empty or still what the last fetch put there. */
function fillable(value: string, fetched: string | undefined): boolean {
  return value.trim() === "" || value === fetched;
}

/**
 * The rupee value of money received in another currency, on the day it arrived: what the
 * books, Money -> Payouts due and the Payments page value the payment at. Worked out via the
 * riyal (SAR per USD × rupees per SAR) or directly (rupees per USD); the day's market rates
 * can be fetched, every figure stays editable, a typed one is never overwritten by a fetch,
 * and the arithmetic is shown so it can be checked before saving.
 */
export function RupeeValue({
  id,
  currency,
  net,
  receivedOn,
  receivedCurrency,
  receivedRate,
  receivedRateFixed = false,
  fxRate,
  inrCredited,
  error,
  onChange,
}: RupeeValueProps) {
  const groupName = useId();
  const quotes = useQuoteLookup();
  const [market, setMarket] = useState<FxQuote[] | null>(null);
  const [chosenPath, choosePath] = useState<RupeePath | null>(null);
  const [driver, setDriver] = useState<Driver>("rate");
  // The SAR rate when the money did not come in riyals (it is the arrival's rate when it did).
  const [ownPerUnit, setOwnPerUnit] = useState("");
  const [perOtherTyped, setPerOther] = useState("");
  const [rupeesGot, setRupeesGot] = useState("");
  const fetched = useRef<Fetched>({});

  const received =
    receivedCurrency && receivedCurrency !== currency && receivedCurrency !== "INR"
      ? receivedCurrency
      : "";
  const via = received || VIA_CURRENCY;
  const path = chosenPath ?? defaultPath(received);
  const perUnit = received ? receivedRate : ownPerUnit;
  const perOther = driver === "chain" ? perOtherTyped : (per(fxRate, perUnit) ?? perOtherTyped);
  // The rupee rate follows the figures it is worked out from, even when one of them changes
  // elsewhere on the form (the SAR rate under How it arrived, the amount).
  const worked =
    driver === "chain" && path === "via"
      ? times(perUnit, perOther)
      : driver === "rupees" && received
        ? per(rupeesGot, net)
        : null;
  // What the figures are when a fetch lands, not when it was asked for: one typed meanwhile
  // must not be overwritten.
  const now = useRef({ driver, fxRate, perOtherTyped, perUnit, perOther });
  now.current = { driver, fxRate, perOtherTyped, perUnit, perOther };
  useEffect(() => {
    if (worked !== null && worked !== fxRate) onChange({ fx_rate: worked });
  }, [worked, fxRate, onChange]);

  const rupees =
    positive(net) && positive(fxRate)
      ? (Math.round(Number(net) * 100 * Number(fxRate)) / 100).toFixed(2)
      : null;
  const credited = positive(inrCredited) !== null;

  function typeRate(value: string): void {
    setDriver("rate");
    setRupeesGot("");
    onChange({ fx_rate: value });
  }

  function typePerOther(value: string): void {
    setDriver("chain");
    setRupeesGot("");
    setPerOther(value);
  }

  function typeRupeesGot(value: string): void {
    setDriver("rupees");
    setRupeesGot(value);
  }

  function fillDirect([direct]: FxQuote[]): void {
    const last = fetched.current;
    const { driver, fxRate, perOtherTyped } = now.current;
    const mayFill =
      driver === "rate"
        ? fillable(fxRate, last.fx)
        : driver === "chain" && fillable(perOtherTyped, last.perOther);
    if (!direct || !mayFill) return;
    const rate = plainRate(direct.rate);
    fetched.current = { ...last, fx: rate };
    setDriver("rate");
    setRupeesGot("");
    onChange({ fx_rate: rate });
  }

  function fillVia([toVia, viaToInr]: FxQuote[]): void {
    if (!toVia || !viaToInr) return;
    const last = fetched.current;
    const { driver, fxRate, perOtherTyped, perUnit, perOther } = now.current;
    const fillUnit = !(received && receivedRateFixed) && fillable(perUnit, last.perUnit);
    const unit = fillUnit ? plainRate(toVia.rate) : perUnit;
    if (fillUnit && received) onChange({ received_rate: unit });
    if (fillUnit && !received) setOwnPerUnit(unit);
    // The rupees per SAR, unless the rupee rate is the director's own (typed, or rupees got).
    const fillOther =
      driver === "chain"
        ? fillable(perOtherTyped, last.perOther)
        : driver === "rate" && fillable(fxRate, last.fx);
    const other = fillOther ? plainRate(viaToInr.rate) : perOther;
    if (fillOther) {
      setPerOther(other);
      setDriver("chain");
      setRupeesGot("");
    }
    fetched.current = {
      perUnit: fillUnit ? unit : last.perUnit,
      perOther: fillOther ? other : last.perOther,
      fx: fillOther || driver === "chain" ? (times(unit, other) ?? undefined) : last.fx,
    };
  }

  async function fetchRates(): Promise<void> {
    const found =
      path === "via"
        ? await quotes.lookup(receivedOn, [currency, via], [via, "INR"])
        : await quotes.lookup(receivedOn, [currency, "INR"]);
    if (!found) return;
    setMarket(found);
    if (path === "via") fillVia(found);
    else fillDirect(found);
  }

  const [toVia, viaToInr] = path === "via" && market?.length === 2 ? market : [];
  const direct = path === "direct" && market?.length === 1 ? market[0] : undefined;
  // A recorded payment's riyals without their rate: nothing to work the rupees out from.
  const fetchBlocked =
    path === "via" && received !== "" && receivedRateFixed && !positive(receivedRate);
  const sources = [...new Set((market ?? []).map((quote) => quote.source))].join(" and ");
  const paths: { value: RupeePath; label: string }[] = [
    { value: "via", label: `Via ${via} (${currency} → ${via} → INR)` },
    { value: "direct", label: `Direct (${currency} → INR)` },
  ];

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Rupee value
      </legend>
      <p className="text-xs text-muted-foreground">
        What this money is worth in rupees on the day it arrived. The books and Payouts due value
        the payment at this rate (Books → Exchange rates only covers a payment without one), so
        check it against what the bank or exchange actually gave.
      </p>
      <div
        role="radiogroup"
        aria-label="How the rupee rate is worked out"
        className="flex flex-wrap gap-x-6 gap-y-2"
      >
        {paths.map((option) => (
          <label
            key={option.value}
            htmlFor={`${id}-path-${option.value}`}
            className="flex items-center gap-2 text-sm"
          >
            <input
              id={`${id}-path-${option.value}`}
              type="radio"
              name={groupName}
              checked={path === option.value}
              onChange={() => choosePath(option.value)}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
            />
            {option.label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => void fetchRates()}
          disabled={!receivedOn || quotes.busy || fetchBlocked}
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          {quotes.busy ? "Fetching…" : `Fetch rate for ${formatDate(receivedOn)}`}
        </Button>
        {market?.[0] && (
          <span className="text-xs text-muted-foreground">
            Market rates from {sources}, published {formatDate(market[0].published_on)}
          </span>
        )}
      </div>
      <RequestError error={quotes.error} />
      <div className="grid gap-4 md:grid-cols-2">
        {path === "via" && !received && (
          <TextField
            id={`${id}-per-unit`}
            label={`${via} per ${currency}`}
            inputMode="decimal"
            value={ownPerUnit}
            onChange={setOwnPerUnit}
            hint={`The rate the ${currency} was changed into ${via} at`}
          />
        )}
        {path === "via" && (
          <TextField
            id={`${id}-per-other`}
            label={`Rupees per ${via}`}
            inputMode="decimal"
            value={perOther}
            onChange={typePerOther}
            hint={
              received
                ? `With the ${via} rate under How it arrived, it gives the rupees per ${currency}`
                : `With the ${via} rate, it gives the rupees per ${currency}`
            }
          />
        )}
        <TextField
          id={`${id}-fx`}
          label={`Rupees per ${currency}`}
          inputMode="decimal"
          value={fxRate}
          onChange={typeRate}
          error={error}
          hint="The rate saved; edit it to the rate you actually got"
        />
        {received && (
          <TextField
            id={`${id}-got`}
            label={`Rupees you got for the ${received}`}
            inputMode="decimal"
            value={rupeesGot}
            onChange={typeRupeesGot}
            hint="If you know the exact rupees, type them: the rate follows"
          />
        )}
      </div>
      {path === "via" && positive(perUnit) && positive(perOther) && positive(fxRate) && (
        <p className="text-sm">
          {`${via} ${plainRate(perUnit)} per ${currency} × INR ${plainRate(perOther)} per ${via} = INR ${fxRate.trim()} per ${currency}`}
          {toVia && Number(toVia.rate) !== Number(perUnit) && (
            <span className="block text-xs text-muted-foreground">
              {`The market rate that day was ${via} ${plainRate(toVia.rate)} per ${currency}; the rate above is the one this money was taken at.`}
            </span>
          )}
          {viaToInr && Number(viaToInr.rate) !== Number(perOther) && (
            <span className="block text-xs text-muted-foreground">
              {`The market rate that day was INR ${plainRate(viaToInr.rate)} per ${via}; your figures are kept.`}
            </span>
          )}
        </p>
      )}
      {direct && positive(fxRate) && Number(direct.rate) !== Number(fxRate) && (
        <p className="text-xs text-muted-foreground">
          {`The market rate that day was INR ${plainRate(direct.rate)} per ${currency}; your figure is kept.`}
        </p>
      )}
      {rupees && net && (
        <p className="text-sm">
          <strong>{`${formatMoney(net, currency)} net × ${fxRate.trim()} = ${formatInr(rupees)}`}</strong>
          {credited && (
            <span className="block text-xs text-muted-foreground">
              The INR credited by the bank counts instead, once it is given.
            </span>
          )}
        </p>
      )}
    </fieldset>
  );
}
